import { isItalianHoliday } from './holidays';
// @ts-ignore
import * as XLSXStyle from 'xlsx-js-style';

export const inpasMonthNames = [
  'Gennaio',
  'Febbraio',
  'Marzo',
  'Aprile',
  'Maggio',
  'Giugno',
  'Luglio',
  'Agosto',
  'Settembre',
  'Ottobre',
  'Novembre',
  'Dicembre',
];

export interface InpasDailyValidationDetail {
  giorno: number;
  ore_presenza: number;
  ore_ferie: number;
  ore_permesso: number;
  altre_assenze: number;
  motivazione_assenza?: string | null;
  ore_attese: number;
  ore_totali: number;
  ha_anomalia: boolean;
  differenza_ore: number;
}

export interface InpasPresenceValidation {
  id: number;
  employee_id: number;
  user_id: number;
  year: number;
  month: number;
  total_presence_hours: number;
  total_leave_hours: number;
  total_permission_hours: number;
  total_other_absence_hours: number;
  total_working_days: number;
  daily_details: InpasDailyValidationDetail[];
  employee_name: string;
  employee_surname: string;
  employee_codice_fiscale?: string;
  employee_matricola?: string | null;
  employee_sede?: string | null;
  employee_dipartimento?: string | null;
  employee_weekly_hours?: number | null;
  employee_weekly_days?: number | null;
  employee_lunch_break_minutes?: number | null;
  validated_at: string;
  is_provisional: boolean;
}

const cleanFileName = (value: string) => value.replace(/[\\\/?*\[\]:<>|"]/g, '_').trim();

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const buildWorkbook = (validation: InpasPresenceValidation, year: number, month: number) => {
  const wb = XLSXStyle.utils.book_new();
  const daysInMonth = new Date(year, month, 0).getDate();
  const inpasData: any[][] = [];

  const employeeName = `${validation.employee_surname || ''} ${validation.employee_name || ''}`.trim().toUpperCase();
  const sede = validation.employee_sede || '';
  const fullName = sede ? `${employeeName} - ${sede}` : employeeName;

  inpasData.push(['inpas', '', '', '', '', '', '']);
  inpasData.push(['Mese', inpasMonthNames[month - 1], '', 'Anno', year.toString(), '', '']);
  inpasData.push(['Nominativo e Sede', fullName, '', '', '', '', '']);
  inpasData.push([]);
  inpasData.push(['giorno', 'ore presenza', 'ore ferie', 'ore permesso', 'altre assenze', '', '']);
  inpasData.push(['', '', '', '', 'n.ore', 'motivazione', '']);

  for (let day = 1; day <= daysInMonth; day++) {
    const detail = validation.daily_details?.find((entry) => entry.giorno === day);

    let orePresenza = '';
    let oreFerie = '';
    let orePermesso = '';
    let altreAssenzeOre = '';
    let altreAssenzeMotivazione = '';

    if (detail) {
      if (detail.ore_presenza > 0) {
        orePresenza = Math.round(detail.ore_presenza).toString();
      }
      if (detail.ore_ferie > 0) {
        oreFerie = Math.round(detail.ore_ferie).toString();
      }
      if (detail.ore_permesso > 0) {
        orePermesso = Math.round(detail.ore_permesso).toString();
      }
      if (detail.altre_assenze > 0) {
        altreAssenzeOre = Math.round(detail.altre_assenze).toString();
        altreAssenzeMotivazione = detail.motivazione_assenza || 'ALTRE ASSENZE';
      }
    }

    inpasData.push([
      day.toString(),
      orePresenza,
      oreFerie,
      orePermesso,
      altreAssenzeOre,
      altreAssenzeMotivazione,
      '',
    ]);
  }

  inpasData.push([]);
  inpasData.push(['Ai fini della validita del documento e necessario che siano presenti le firme richieste in calce.', '', '', '', '', '', '']);
  inpasData.push([]);
  inpasData.push(['Firma Operatore', '', '', '', 'note', '', '']);
  inpasData.push(['', '', '', '', '', '', '']);
  inpasData.push(['', '', '', '', '', '', '']);

  const ws = XLSXStyle.utils.aoa_to_sheet(inpasData);
  const lightBlueFill = { fgColor: { rgb: 'B3D9FF' } };
  const greyFill = { fgColor: { rgb: 'D3D3D3' } };

  const headerStyle = {
    fill: lightBlueFill,
    font: { bold: true, color: { rgb: '000000' } },
    alignment: { horizontal: 'center', vertical: 'center', wrapText: true },
    border: {
      top: { style: 'thin', color: { rgb: '000000' } },
      bottom: { style: 'thin', color: { rgb: '000000' } },
      left: { style: 'thin', color: { rgb: '000000' } },
      right: { style: 'thin', color: { rgb: '000000' } },
    },
  };

  const defaultStyle = {
    alignment: { horizontal: 'center', vertical: 'center' },
    border: {
      top: { style: 'thin', color: { rgb: '000000' } },
      bottom: { style: 'thin', color: { rgb: '000000' } },
      left: { style: 'thin', color: { rgb: '000000' } },
      right: { style: 'thin', color: { rgb: '000000' } },
    },
  };

  const range = XLSXStyle.utils.decode_range(ws['!ref'] || 'A1');

  for (let row = range.s.r; row <= range.e.r; row++) {
    for (let col = range.s.c; col <= range.e.c; col++) {
      const cellAddress = XLSXStyle.utils.encode_cell({ r: row, c: col });
      if (!ws[cellAddress]) {
        ws[cellAddress] = { v: '', t: 's' };
      }

      if (row === 0 && col === 0) {
        ws[cellAddress].s = {
          ...headerStyle,
          font: { bold: true, size: 14, color: { rgb: '000080' } },
        };
      } else if (row === 1 && (col === 0 || col === 1 || col === 3 || col === 4)) {
        ws[cellAddress].s = headerStyle;
      } else if (row === 2 && (col === 0 || col === 1)) {
        ws[cellAddress].s = headerStyle;
      } else if ((row === 4 || row === 5) && col <= 5) {
        ws[cellAddress].s = headerStyle;
      } else if (row >= 6 && row < 6 + daysInMonth && col === 0) {
        const dayNum = row - 5;
        const date = new Date(year, month - 1, dayNum);
        ws[cellAddress].s = {
          ...defaultStyle,
          fill: (isItalianHoliday(date) || date.getDay() === 0 || date.getDay() === 6) ? greyFill : undefined,
        };
      } else if (row >= 6 && row < 6 + daysInMonth && col <= 6) {
        ws[cellAddress].s = defaultStyle;
      } else if (row === range.e.r - 2 && (col === 0 || col === 4)) {
        ws[cellAddress].s = headerStyle;
      } else {
        ws[cellAddress].s = defaultStyle;
      }
    }
  }

  ws['!cols'] = [
    { wch: 8 },
    { wch: 12 },
    { wch: 10 },
    { wch: 25 },
    { wch: 10 },
    { wch: 25 },
    { wch: 10 },
  ];

  ws['!rows'] = [];
  for (let row = 0; row <= range.e.r; row++) {
    ws['!rows'][row] = { hpt: row === 4 || row === 5 ? 25 : 20 };
  }

  if (!ws['!merges']) {
    ws['!merges'] = [];
  }
  ws['!merges'].push({ s: { r: 4, c: 4 }, e: { r: 4, c: 5 } });

  XLSXStyle.utils.book_append_sheet(wb, ws, 'Cartellino');
  return wb;
};

const downloadWorkbook = (wb: any, filename: string) => {
  const wbout = XLSXStyle.write(wb, { bookType: 'xlsx', type: 'array' });
  const blob = new Blob([wbout], { type: 'application/octet-stream' });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

export const downloadMassiveInpasExport = async (
  validations: InpasPresenceValidation[],
  year: number,
  month: number
) => {
  for (let index = 0; index < validations.length; index++) {
    const validation = validations[index];
    const wb = buildWorkbook(validation, year, month);
    const codiceFiscale = validation.employee_codice_fiscale || 'NOCF';
    const cognome = (validation.employee_surname || '').trim();
    const nome = (validation.employee_name || '').trim();
    const nomeCompleto = `${cognome} ${nome}`.trim();
    const filename = `${cleanFileName(codiceFiscale)} - ${cleanFileName(nomeCompleto)}.xlsx`;

    downloadWorkbook(wb, filename);

    if (index < validations.length - 1) {
      await wait(200);
    }
  }
};
